/**
 * Knowledge Base Service - Handles API interactions for knowledge base management
 */

import axios from 'axios';
import { post, put } from '../utils/apiUtils';

// Use central base API URL (can be set via env: API_URL)
const BASE_URL = process.env.API_URL || '/api';
const API_URL = `${BASE_URL}/v1/bluemormon/knowledgebase`;
const CHAT_LIST_API = `/api/v1/persistence/conversation`;
const MESSAGE_LIST_API = `/api/v1/persistence/message`;
const DOC_META_API = `/api/v1/persistence/document_metadata`;
const STREAM_CHAT_API = `${process.env.REACT_APP_API_BASE_URL}/api/v1/sunlife_poc/sl_poc/agent/query/stream`;


/**
 * Ingest single File 
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const ingestFileToKb = async (knowledgeBaseId, fileNameList, otherOptions, pii_flag) => {
    let payload = {
        document_urls: fileNameList,
        payload: otherOptions,
        "async_execution": true,
        "mask_pii_data": pii_flag
    }
    try {
        const response = await post(`${API_URL}/${knowledgeBaseId}/ingest`, payload);
        return response;
    } catch (error) {
        console.error(`Error uploading file to knowledge base ${knowledgeBaseId}:`, error);
        throw error;
    }
};

/**
 * Get all Chat Transactions with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getAllChatsByFilter = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${CHAT_LIST_API}/fetch?query=${query}`, { filter: filter, sort });
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Get all Chat Messages with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getAllMessagesByFilter = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${MESSAGE_LIST_API}/fetch?query=${query}`, { filter: filter, sort });
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Create ChatList
 * @param {Object} chatpayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const createChatForKb = async (chatpayload) => {
    try {
        const response = await post(`${CHAT_LIST_API}`, chatpayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Create MessageList
 * @param {Object} messagepayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const createMessageForChat = async (messagepayload) => {
    try {
        const response = await post(`${MESSAGE_LIST_API}`, messagepayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};

/**
 * Update ChatList
 * @param {Object} chatpayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const updateChatForKb = async (chatpayload) => {
    try {
        const response = await put(`${CHAT_LIST_API}`, chatpayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};


/**
 * Search within a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Promise resolving to search results
 */
export const searchKnowledgeBase = async (knowledgeBaseId, query, context, conversation_id, type) => {
    try {
        const response = await post(`${API_URL}/${type}`, {
            query: query,
            context: context,
            knowledge_base: knowledgeBaseId,
            conversation_id: conversation_id
        });
        return response.response;
    } catch (error) {
        console.error(`Error searching knowledge base ${knowledgeBaseId}:`, error);
        throw error;
    }
};

/**
 * Search Doc meta data
 * @param {string} query - Search query
 * @param {Object} filter - Search options
 * @param {Object} sort - Search options
 * @returns {Promise<Object>} Promise resolving to search results
 */
export const getDocMetaDataWithFilters = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${DOC_META_API}/fetch?query=${query}`, {
            filter: filter,
            sort: sort
        });
        return response;
    } catch (error) {
        console.error(`Error searching knowledge base:`, error);
        throw error;
    }
};

export default {
    ingestFileToKb,
    getAllChatsByFilter,
    searchKnowledgeBase,
    getAllMessagesByFilter,
    getDocMetaDataWithFilters
};

/**
 * Stream agent query results from the backend and invoke onMessage for each parsed object.
 * Expects newline-delimited JSON or chunked JSON objects.
 * @param {{query:string, session_id?:string, user_id?:string}} payload
 * @param {(obj:Object)=>void} onMessage
 */
export const streamAgentQuery = async (payload = {}, onMessage) => {
    const endpoint = "https://product-dev-bluemormon.metamorphosys.co.in/services/api/v1/sunlife_poc/sl_poc/agent/query/stream";
    const token = localStorage.getItem('authToken');

    const res = await fetch(STREAM_CHAT_API, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {})
        },
        body: JSON.stringify(payload)
    });

    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Stream request failed (${res.status}): ${text}`);
    }

    if (!res.body) {
        const json = await res.json();
        if (typeof onMessage === 'function') onMessage(json);
        if (typeof onMessage === 'function') onMessage({ type: 'stream_done' });
        return json;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let done = false;

    // helper: try to parse JSON, tolerant of single quotes for keys/values
    const tryParseJson = (text) => {
        try { return JSON.parse(text); } catch (e) {
            // try to replace single-quoted keys/values with double quotes
            try {
                let s = text.replace(/(['\"])?([a-zA-Z0-9_]+)\1\s*:/g, '"$2":');
                s = s.replace(/:\s*'([^']*)'/g, ': "$1"');
                s = s.replace(/'([^']*)'/g, '"$1"');
                return JSON.parse(s);
            } catch (e2) {
                return null;
            }
        }
    };

    // helper: find matching closing brace for first '{' at pos start
    const findMatchingBrace = (str, start) => {
        if (start < 0) return -1;
        let depth = 0;
        for (let i = start; i < str.length; i++) {
            const ch = str[i];
            if (ch === '{') depth++;
            else if (ch === '}') {
                depth--;
                if (depth === 0) return i;
            }
        }
        return -1;
    };

    while (!done) {
        const { value, done: rDone } = await reader.read();
        done = rDone;
        if (value) {
            buffer += decoder.decode(value, { stream: true });

            // extract all complete JSON objects using brace matching
            while (true) {
                const start = buffer.indexOf('{');
                if (start === -1) break;
                const end = findMatchingBrace(buffer, start);
                if (end === -1) break; // need more data

                const candidate = buffer.slice(start, end + 1);
                buffer = buffer.slice(end + 1);

                const obj = tryParseJson(candidate.trim());
                if (obj && typeof onMessage === 'function') {
                    try { onMessage(obj); } catch (e) { console.error('onMessage error', e); }
                }
            }

            // if there's remaining non-brace text (e.g., raw content fragments like "Hello")
            const rem = buffer.trim();
            if (rem && buffer.indexOf('{') === -1) {
                // ignore stray single braces
                if (rem !== '}' && rem !== '{') {
                    if (typeof onMessage === 'function') {
                        try { onMessage({ type: 'final_response', content: rem }); } catch (e) { console.error('onMessage error', e); }
                    }
                }
                buffer = '';
            }
        }
    }

    // final flush: parse any remaining complete object
    buffer = buffer.trim();
    if (buffer) {
        const start = buffer.indexOf('{');
        if (start !== -1) {
            const end = findMatchingBrace(buffer, start);
            if (end !== -1) {
                const candidate = buffer.slice(start, end + 1);
                const obj = tryParseJson(candidate.trim());
                if (obj && typeof onMessage === 'function') onMessage(obj);
            }
        } else if (buffer !== '}' && buffer !== '{') {
            if (typeof onMessage === 'function') onMessage({ type: 'final_response', content: buffer });
        }
    }

    // signal that stream finished
    if (typeof onMessage === 'function') onMessage({ type: 'stream_done' });
};


/**
 * Helper: Try to parse JSON, tolerant of single quotes for keys/values
 */
const tryParseJson = (text) => {
    try {
        return JSON.parse(text);
    } catch (e) {
        try {
            let s = text.replace(/(['\"])?([a-zA-Z0-9_]+)\1\s*:/g, '"$2":');
            s = s.replace(/:\s*'([^']*)'/g, ': "$1"');
            s = s.replace(/'([^']*)'/g, '"$1"');
            return JSON.parse(s);
        } catch (e2) {
            return null;
        }
    }
};

/**
 * Helper: Find matching closing brace for first '{' at pos start
 */
const findMatchingBrace = (str, start) => {
    if (start < 0) return -1;
    let depth = 0;
    for (let i = start; i < str.length; i++) {
        const ch = str[i];
        if (ch === '{') depth++;
        else if (ch === '}') {
            depth--;
            if (depth === 0) return i;
        }
    }
    return -1;
};


